import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  FilePdf, Plus, X, TrashSimple, DownloadSimple, IdentificationBadge,
  MagnifyingGlass, Copy, FunnelSimple, PencilSimple, Printer,
} from "@phosphor-icons/react";
import { MultiFilePicker } from "@/components/MultiFilePicker";
import { printFilterSummary } from "@/lib/printSummary";

function fmtDate(iso) {
  if (!iso) return "—";
  const s = String(iso);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString("en-GB", { timeZone: "Asia/Kuala_Lumpur", day: "2-digit", month: "short", year: "numeric" });
}
const isExpired  = (v) => { if (!v) return false; const d = new Date(v); return !Number.isNaN(d.getTime()) && d < new Date(); };
const isSoon     = (v) => { if (!v) return false; const d = new Date(v); if (Number.isNaN(d.getTime())) return false; const days = (d - Date.now()) / 86400000; return days >= 0 && days <= 7; };

export default function Permits() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [permitTypes, setPermitTypes] = useState([]); // [{value,label,is_builtin}]
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);

  const [q, setQ] = useState(params.get("q") || "");
  const [customer, setCustomer] = useState(params.get("customer") || "");
  const [permitType, setPermitType] = useState(params.get("type") || "");
  const [from, setFrom] = useState(params.get("from") || "");
  const [to, setTo] = useState(params.get("to") || "");
  const [sortKey, setSortKey] = useState(params.get("sort") || null);
  const [sortDir, setSortDir] = useState(params.get("dir") === "desc" ? "desc" : "asc");

  useEffect(() => {
    const next = {};
    if (q) next.q = q;
    if (customer) next.customer = customer;
    if (permitType) next.type = permitType;
    if (from) next.from = from;
    if (to) next.to = to;
    if (sortKey) { next.sort = sortKey; next.dir = sortDir; }
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, customer, permitType, from, to, sortKey, sortDir]);

  const loadTypes = async () => {
    try {
      const { data } = await api.get("/permit-types");
      setPermitTypes(data);
    } catch { /* silent */ }
  };
  const load = async () => {
    setLoading(true);
    try {
      const [{ data: rows }, { data: types }] = await Promise.all([
        api.get("/permits"),
        api.get("/permit-types"),
      ]);
      setItems(rows);
      setPermitTypes(types);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const typeLabel = (v) => (permitTypes.find((t) => t.value === v)?.label) || v;

  const del = async (p) => {
    if (!window.confirm(`Delete permit "${p.permit_name}" for ${p.customer_name}?`)) return;
    try {
      await api.delete(`/permits/${p.id}`);
      setItems((xs) => xs.filter((x) => x.id !== p.id));
      toast.success("Permit deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const openPdf = (id) => {
    const t = localStorage.getItem("access_token") || "";
    window.open(`${API}/permits/${id}/download?auth=${encodeURIComponent(t)}`, "_blank");
  };
  const shareLink = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Filtered link copied — share it with your team");
      return;
    } catch {}
    try {
      const ta = document.createElement("textarea");
      ta.value = url; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
      toast.success("Filtered link copied — share it with your team");
    } catch { toast.error("Could not copy — copy the URL manually"); }
  };
  const clearFilters = () => { setQ(""); setCustomer(""); setPermitType(""); setFrom(""); setTo(""); setSortKey(null); setSortDir("asc"); };

  const toggleSort = (key) => {
    if (sortKey !== key) { setSortKey(key); setSortDir("asc"); }
    else if (sortDir === "asc") setSortDir("desc");
    else { setSortKey(null); setSortDir("asc"); }
  };

  const customersInData = useMemo(() => Array.from(new Set(items.map((x) => x.customer_name).filter(Boolean))).sort(), [items]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const cf = customer.trim().toLowerCase();
    return items.filter((p) => {
      if (needle) {
        const hay = `${p.customer_name} ${p.permit_name} ${p.permit_type} ${typeLabel(p.permit_type)} ${p.filename || ""}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      if (cf && !(p.customer_name || "").toLowerCase().includes(cf)) return false;
      if (permitType && p.permit_type !== permitType) return false;
      if (from && (p.permit_date || "") && p.permit_date < from) return false;
      if (to   && (p.permit_date || "") && p.permit_date > to)   return false;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, q, customer, permitType, from, to, permitTypes]);

  const displayed = useMemo(() => {
    if (!sortKey) return filtered;
    const dir = sortDir === "asc" ? 1 : -1;
    const getV = (p) => {
      switch (sortKey) {
        case "customer_name": return (p.customer_name || "").toLowerCase();
        case "permit_name":   return (p.permit_name   || "").toLowerCase();
        case "permit_type":   return (typeLabel(p.permit_type) || "").toLowerCase();
        case "permit_date":   return p.permit_date    || "";
        case "valid_until":   return p.valid_until    || "";
        default: return "";
      }
    };
    return [...filtered].sort((a, b) => {
      const va = getV(a), vb = getV(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return  1 * dir;
      return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, sortKey, sortDir, permitTypes]);

  const hasFilters = q || customer || permitType || from || to || sortKey;

  const onPrint = () => {
    printFilterSummary({
      title: "Permits — Filter Summary",
      filters: [
        q && `Search: "${q}"`,
        customer && `Customer: ${customer}`,
        permitType && `Type: ${typeLabel(permitType)}`,
        from && `From: ${from}`,
        to && `To: ${to}`,
      ].filter(Boolean),
      columns: [
        { key: "customer_name", label: "Customer" },
        { key: "permit_name",   label: "Permit Name" },
        { key: "permit_type",   label: "Type", format: (v) => typeLabel(v) },
        { key: "permit_date",   label: "Permit Date", format: fmtDate },
        { key: "valid_until",   label: "Valid Until", format: (v) => `${fmtDate(v)}${isExpired(v) ? " (EXPIRED)" : isSoon(v) ? " (SOON)" : ""}` },
        { key: "filename",      label: "File" },
      ],
      rows: displayed,
    });
  };

  return (
    <Layout>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono mb-2">Compliance</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-zinc-950">Permits</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-xl">
            Customer permits (Pertanian · Perikanan · LKIM · Others) with attached PDF. Rows highlight in <span className="text-amber-700">amber</span> when expiring within 7 days and <span className="text-red-700">red</span> when expired.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onPrint} data-testid="permit-print-btn"
            className="bg-white border border-zinc-200 text-zinc-800 text-sm px-3 py-2.5 flex items-center gap-2 hover:bg-zinc-50 transition-colors duration-150">
            <Printer size={14} /> Print filter summary
          </button>
          <button onClick={shareLink} data-testid="permit-share-btn"
            className="bg-white border border-zinc-200 text-zinc-800 text-sm px-3 py-2.5 flex items-center gap-2 hover:bg-zinc-50 transition-colors duration-150">
            <Copy size={14} /> Copy share link
          </button>
          <button onClick={() => setShowNew(true)} data-testid="new-permit-btn"
            className="bg-[#002FA7] text-white text-sm px-4 py-2.5 flex items-center gap-2 hover:bg-[#00227A] transition-colors duration-150">
            <Plus size={16} /> New Permit
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-zinc-200 p-3 mb-4 grid grid-cols-1 md:grid-cols-6 gap-2" data-testid="permit-filters">
        <div className="md:col-span-2 relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer, permit name, type…"
            data-testid="permit-search"
            className="w-full text-sm border border-zinc-200 pl-9 pr-3 py-2 focus:outline-none focus:border-[#002FA7]" />
        </div>
        <input list="permit-customers" value={customer} onChange={(e) => setCustomer(e.target.value)}
          placeholder="Customer name…" data-testid="permit-customer-filter"
          className="text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7]" />
        <datalist id="permit-customers">
          {customersInData.map((c) => <option key={c} value={c} />)}
        </datalist>
        <select value={permitType} onChange={(e) => setPermitType(e.target.value)}
          data-testid="permit-type-filter"
          className="text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7] bg-white">
          <option value="">All types</option>
          {permitTypes.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
          data-testid="permit-from"
          className="font-mono text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7]" />
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
          data-testid="permit-to"
          className="font-mono text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7]" />
      </div>
      {hasFilters && (
        <div className="text-xs text-zinc-500 font-mono mb-3 flex items-center gap-3" data-testid="permit-filter-status">
          <FunnelSimple size={12} /> Showing {displayed.length} of {items.length}
          <button onClick={clearFilters} data-testid="permit-clear-filters" className="text-[#002FA7] hover:underline">Clear filters</button>
        </div>
      )}

      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="border-b border-zinc-200 text-[10px] uppercase font-mono text-zinc-500 bg-zinc-50">
              <SortTh label="Customer Name" k="customer_name" sortKey={sortKey} dir={sortDir} onClick={() => toggleSort("customer_name")} />
              <SortTh label="Permit Name"   k="permit_name"   sortKey={sortKey} dir={sortDir} onClick={() => toggleSort("permit_name")} />
              <SortTh label="Type"          k="permit_type"   sortKey={sortKey} dir={sortDir} onClick={() => toggleSort("permit_type")} />
              <SortTh label="Permit Date"   k="permit_date"   sortKey={sortKey} dir={sortDir} onClick={() => toggleSort("permit_date")} />
              <SortTh label="Valid Until"   k="valid_until"   sortKey={sortKey} dir={sortDir} onClick={() => toggleSort("valid_until")} />
              <th className="text-left px-4 py-3">File</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-16 text-zinc-500 font-mono text-sm">Loading…</td></tr>
            ) : displayed.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-16 text-zinc-500">
                <IdentificationBadge size={40} weight="duotone" className="mx-auto text-zinc-300 mb-3" />
                <div className="font-mono text-sm">{items.length === 0 ? "No permits yet — add the first one." : "No permits match these filters."}</div>
              </td></tr>
            ) : displayed.map((p) => {
              const expired = isExpired(p.valid_until);
              const soon = !expired && isSoon(p.valid_until);
              const rowCls = expired ? "bg-red-50/60" : soon ? "bg-amber-50/60" : "hover:bg-zinc-50";
              return (
                <tr key={p.id} className={`border-b border-zinc-100 transition-colors duration-150 ${rowCls}`} data-testid={`permit-row-${p.id}`}>
                  <td className="px-4 py-3 text-zinc-900 font-medium">{p.customer_name}</td>
                  <td className="px-4 py-3 text-zinc-800">{p.permit_name}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center text-[11px] font-mono border border-indigo-200 bg-indigo-50 text-indigo-800 px-2 py-0.5 uppercase">
                      {typeLabel(p.permit_type)}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-zinc-700">{fmtDate(p.permit_date)}</td>
                  <td className="px-4 py-3 font-mono">
                    <span className={expired ? "text-red-700 font-bold" : soon ? "text-amber-700" : "text-zinc-700"}>
                      {fmtDate(p.valid_until)}{expired ? "  · EXPIRED" : soon ? "  · SOON" : ""}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => openPdf(p.id)} data-testid={`permit-view-${p.id}`}
                      className="inline-flex items-center gap-1 text-[#002FA7] text-xs hover:underline">
                      <FilePdf size={14} weight="duotone" /> {p.filename}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => openPdf(p.id)} data-testid={`permit-download-${p.id}`}
                      className="inline-flex items-center gap-1 text-xs text-[#002FA7] px-2 py-1 hover:bg-blue-50 transition-colors duration-150">
                      <DownloadSimple size={14} /> Download
                    </button>
                    <button onClick={() => setEditing(p)} data-testid={`permit-edit-${p.id}`}
                      className="inline-flex items-center gap-1 text-xs text-[#002FA7] px-2 py-1 hover:bg-blue-50 transition-colors duration-150 ml-1">
                      <PencilSimple size={14} /> Edit
                    </button>
                    {(user?.role === "admin" || p.created_by_id === user?.id) && (
                      <button onClick={() => del(p)} data-testid={`permit-delete-${p.id}`}
                        className="inline-flex items-center gap-1 text-xs text-red-700 px-2 py-1 hover:bg-red-50 transition-colors duration-150 ml-1">
                        <TrashSimple size={14} /> Delete
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showNew && (
        <NewPermitModal
          permitTypes={permitTypes}
          canAddType={user?.role === "admin"}
          onTypeAdded={loadTypes}
          onClose={() => setShowNew(false)}
          onCreated={(ps) => { setItems((xs) => [...ps, ...xs]); setShowNew(false); }}
        />
      )}
      {editing && (
        <EditPermitModal
          permit={editing}
          permitTypes={permitTypes}
          onClose={() => setEditing(null)}
          onSaved={(u) => { setItems((xs) => xs.map(x => x.id === u.id ? { ...x, ...u } : x)); setEditing(null); }}
        />
      )}
    </Layout>
  );
}

function SortTh({ label, k, sortKey, dir, onClick }) {
  const active = sortKey === k;
  const arrow = !active ? "↕" : dir === "asc" ? "↑" : "↓";
  return (
    <th className="text-left px-4 py-3 select-none">
      <button type="button" onClick={onClick} data-testid={`sort-${k}`}
        className={`inline-flex items-center gap-1 uppercase tracking-[0.05em] hover:text-zinc-900 transition-colors duration-150 ${active ? "text-zinc-900" : "text-zinc-500"}`}>
        {label}
        <span className={`font-mono text-[11px] ${active ? "text-[#002FA7]" : "text-zinc-400"}`}>{arrow}</span>
      </button>
    </th>
  );
}

function NewPermitModal({ permitTypes, canAddType, onTypeAdded, onClose, onCreated }) {
  const [customerName, setCustomerName] = useState("");
  const [permitName, setPermitName] = useState("");
  const [permitType, setPermitType] = useState(permitTypes[0]?.value || "pertanian");
  const [permitDate, setPermitDate] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [addingType, setAddingType] = useState(false);
  const [newTypeLabel, setNewTypeLabel] = useState("");
  const [creatingType, setCreatingType] = useState(false);

  const createType = async () => {
    const label = newTypeLabel.trim();
    if (!label) { toast.error("Type name required"); return; }
    setCreatingType(true);
    try {
      const { data } = await api.post("/permit-types", { label });
      await onTypeAdded();
      setPermitType(data.value);
      setNewTypeLabel("");
      setAddingType(false);
      toast.success(`Permit type "${data.label}" added`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreatingType(false); }
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!customerName.trim()) { toast.error("Customer name is required"); return; }
    if (!permitName.trim())   { toast.error("Permit name is required");   return; }
    if (!files.length)        { toast.error("Please attach at least one file"); return; }
    setSaving(true);
    try {
      const created = [];
      for (let i = 0; i < files.length; i++) {
        const fd = new FormData();
        fd.append("customer_name", customerName.trim());
        fd.append("permit_name", permitName.trim());
        fd.append("permit_type", permitType);
        fd.append("permit_date", permitDate);
        fd.append("valid_until", validUntil);
        fd.append("file", files[i]);
        const { data } = await api.post("/permits", fd, { headers: { "Content-Type": "multipart/form-data" } });
        created.push(data);
      }
      toast.success(created.length === 1 ? "Permit added" : `${created.length} permits added`);
      onCreated(created);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="new-permit-modal">
      <form onSubmit={submit} className="bg-white border border-zinc-200 shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-3">
          <div className="flex items-center gap-2">
            <IdentificationBadge size={18} className="text-[#002FA7]" />
            <div className="font-display font-black text-lg tracking-tight text-zinc-950">New Permit</div>
          </div>
          <button type="button" onClick={onClose} data-testid="permit-close-btn" className="text-zinc-500 hover:text-zinc-900 p-1">
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <Fld label="Customer Name" required>
            <input value={customerName} onChange={(e) => setCustomerName(e.target.value)} data-testid="permit-customer-input"
              className="w-full text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7]" />
          </Fld>
          <Fld label="Permit Name" required>
            <input value={permitName} onChange={(e) => setPermitName(e.target.value)} data-testid="permit-name-input"
              className="w-full text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7]" />
          </Fld>
          <Fld label="Permit Type">
            <div className="flex items-center gap-2">
              <select value={permitType} onChange={(e) => setPermitType(e.target.value)} data-testid="permit-type-select"
                className="flex-1 text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7] bg-white">
                {permitTypes.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              {canAddType && (
                <button type="button" onClick={() => setAddingType((v) => !v)} data-testid="permit-add-type-btn"
                  className="bg-white border border-zinc-200 text-zinc-700 px-2.5 py-2 hover:bg-zinc-50 transition-colors duration-150"
                  title="Add new permit type">
                  <Plus size={14} />
                </button>
              )}
            </div>
            {addingType && (
              <div className="mt-2 flex items-center gap-2" data-testid="permit-new-type-row">
                <input value={newTypeLabel} onChange={(e) => setNewTypeLabel(e.target.value)}
                  placeholder="e.g. FAMA, DVS, KKM…" data-testid="permit-new-type-input"
                  className="flex-1 text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7]" />
                <button type="button" onClick={createType} disabled={creatingType} data-testid="permit-new-type-save"
                  className="text-sm bg-[#002FA7] text-white px-3 py-2 hover:bg-[#00227A] disabled:opacity-50">
                  {creatingType ? "…" : "Add"}
                </button>
              </div>
            )}
          </Fld>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Permit Date">
              <input type="date" value={permitDate} onChange={(e) => setPermitDate(e.target.value)} data-testid="permit-date-input"
                className="w-full font-mono text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7]" />
            </Fld>
            <Fld label="Valid Until">
              <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} data-testid="permit-valid-input"
                className="w-full font-mono text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7]" />
            </Fld>
          </div>
          <Fld label="Files (any type — creates one permit per file)" required>
            <MultiFilePicker files={files} onChange={setFiles} testid="permit-file" required hint="PDF, image, Excel, Word, ZIP…"/>
          </Fld>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-zinc-200 px-5 py-3 bg-zinc-50">
          <button type="button" onClick={onClose} data-testid="permit-cancel-btn"
            className="text-sm border border-zinc-200 bg-white px-3 py-2 hover:bg-zinc-100 transition-colors duration-150">Cancel</button>
          <button type="submit" disabled={saving} data-testid="permit-submit-btn"
            className="text-sm bg-[#002FA7] text-white px-4 py-2 hover:bg-[#00227A] disabled:opacity-50 transition-colors duration-150">
            {saving ? "Uploading…" : "Save permit"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Fld({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-1">
        {label}{required && <span className="text-[#FF4F00]"> *</span>}
      </div>
      {children}
    </label>
  );
}

function EditPermitModal({ permit, permitTypes, onClose, onSaved }) {
  const [customerName, setCustomerName] = useState(permit.customer_name || "");
  const [permitName, setPermitName] = useState(permit.permit_name || "");
  const [permitType, setPermitType] = useState(permit.permit_type || permitTypes[0]?.value || "");
  const [permitDate, setPermitDate] = useState(permit.permit_date || "");
  const [validUntil, setValidUntil] = useState(permit.valid_until || "");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e?.preventDefault();
    if (!customerName.trim()) { toast.error("Customer name is required"); return; }
    if (!permitName.trim()) { toast.error("Permit name is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/permits/${permit.id}`, {
        customer_name: customerName.trim(), permit_name: permitName.trim(),
        permit_type: permitType, permit_date: permitDate, valid_until: validUntil,
      });
      toast.success("Permit updated"); onSaved(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="edit-permit-modal">
      <form onSubmit={submit} className="bg-white border border-zinc-200 shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-3">
          <div className="font-display font-black text-lg tracking-tight text-zinc-950">Edit Permit</div>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-zinc-900 p-1"><X size={16}/></button>
        </div>
        <div className="p-5 space-y-4">
          <Fld label="Customer Name" required><input value={customerName} onChange={(e) => setCustomerName(e.target.value)} data-testid="permit-edit-customer" className="w-full text-sm border border-zinc-200 px-3 py-2"/></Fld>
          <Fld label="Permit Name" required><input value={permitName} onChange={(e) => setPermitName(e.target.value)} data-testid="permit-edit-name" className="w-full text-sm border border-zinc-200 px-3 py-2"/></Fld>
          <Fld label="Permit Type">
            <select value={permitType} onChange={(e) => setPermitType(e.target.value)} data-testid="permit-edit-type" className="w-full text-sm border border-zinc-200 px-3 py-2 bg-white">
              {permitTypes.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Fld>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Permit Date"><input type="date" value={permitDate} onChange={(e) => setPermitDate(e.target.value)} data-testid="permit-edit-date" className="w-full font-mono text-sm border border-zinc-200 px-3 py-2"/></Fld>
            <Fld label="Valid Until"><input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} data-testid="permit-edit-valid" className="w-full font-mono text-sm border border-zinc-200 px-3 py-2"/></Fld>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-zinc-200 px-5 py-3 bg-zinc-50">
          <button type="button" onClick={onClose} className="text-sm border border-zinc-200 bg-white px-3 py-2 hover:bg-zinc-100">Cancel</button>
          <button type="submit" disabled={saving} data-testid="permit-edit-save" className="text-sm bg-[#002FA7] text-white px-4 py-2 hover:bg-[#00227A] disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}
